import { useCallback, useEffect, useRef, useState } from 'react';
import { getPdfStatus, getStewardshipPdf } from '../../utils/pdf-util';

export type StewardshipPdfState = 'idle' | 'pending' | 'success' | 'error';

const MAX_PDF_STATUS_ATTEMPTS = 36;
const POLL_INTERVAL_MS = 10000;

export function useStewardshipPdfDownload() {
  const [pdfState, setPdfState] = useState<StewardshipPdfState>('idle');
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfPath, setPdfPath] = useState<string | null>(null);

  const pdfIdRef = useRef<number | null>(null);
  const pdfGenerationRef = useRef(0);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollAttemptsRef = useRef(0);
  const pdfSettledRef = useRef(false);
  const isBusyRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    pdfGenerationRef.current += 1;
    pdfIdRef.current = null;
    pdfSettledRef.current = false;
    isBusyRef.current = false;
    stopPolling();
    setPdfState('idle');
    setPdfError(null);
    setPdfPath(null);
    return pdfGenerationRef.current;
  }, [stopPolling]);

  const isCurrentGeneration = (generation: number) => generation === pdfGenerationRef.current;

  const startPolling = useCallback(
    (generation: number) => {
      pollAttemptsRef.current = 0;
      pollRef.current = setInterval(async () => {
        pollAttemptsRef.current += 1;
        if (!isCurrentGeneration(generation) || pdfSettledRef.current) {
          return;
        }
        if (pollAttemptsRef.current >= MAX_PDF_STATUS_ATTEMPTS) {
          pdfSettledRef.current = true;
          isBusyRef.current = false;
          stopPolling();
          setPdfState('error');
          setPdfError('PDF generation timed out.');
          return;
        }
        try {
          const pdfId = pdfIdRef.current;
          if (pdfId == null) {
            return;
          }
          const status = await getPdfStatus(pdfId);
          if (!isCurrentGeneration(generation) || pdfSettledRef.current) {
            return;
          }
          if (status.status === 'success' && status.pdfPath) {
            pdfSettledRef.current = true;
            isBusyRef.current = false;
            stopPolling();
            setPdfPath(status.pdfPath);
            setPdfState('success');
          } else if (status.status === 'error' || status.status === 'success') {
            pdfSettledRef.current = true;
            isBusyRef.current = false;
            stopPolling();
            setPdfState('error');
            setPdfError('PDF generation failed.');
          }
        } catch {
          if (!isCurrentGeneration(generation) || pdfSettledRef.current) {
            return;
          }
          pdfSettledRef.current = true;
          isBusyRef.current = false;
          stopPolling();
          setPdfState('error');
          setPdfError('PDF generation failed.');
        }
      }, POLL_INTERVAL_MS);
    },
    [stopPolling],
  );

  const download = useCallback(
    async (pid: number, meetingId: number, timeZone: string) => {
      if (isBusyRef.current) {
        return;
      }
      const generation = reset();
      isBusyRef.current = true;
      setPdfState('pending');
      try {
        const res = await getStewardshipPdf(pid, meetingId, timeZone);
        if (!isCurrentGeneration(generation)) {
          return;
        }
        if (res?.status >= 400 || !res?.data?.pdfId) {
          throw new Error('dispatch failed');
        }
        pdfIdRef.current = res.data.pdfId;
        startPolling(generation);
      } catch {
        if (!isCurrentGeneration(generation)) {
          return;
        }
        isBusyRef.current = false;
        setPdfState('error');
        setPdfError('Failed to start PDF generation.');
      }
    },
    [reset, startPolling],
  );

  useEffect(
    () => () => {
      pdfGenerationRef.current += 1;
      stopPolling();
    },
    [stopPolling],
  );

  return { pdfState, pdfError, pdfPath, download, reset };
}
